import { useState, useEffect } from 'react'
import { Theme, webLightTheme, webDarkTheme } from '@fluentui/react-components'

export type ThemeMode = 'light' | 'dark' | 'auto'

// Create a custom dark theme with the specified background color
const customDarkTheme: Theme = {
    ...webDarkTheme,
    colorNeutralBackground1: '#181A34',
    colorNeutralBackground2: '#1f2347',
    colorNeutralBackground3: '#262a5a',
}

interface UseThemeResult {
    theme: Theme
    themeMode: ThemeMode
    isDarkMode: boolean
    setThemeMode: (mode: ThemeMode) => void
}

// Function to detect theme from Office context
const detectOfficeTheme = (): 'light' | 'dark' | null => {
    try {
        // Check if Office context and officeTheme are available
        if (Office?.context?.officeTheme?.bodyBackgroundColor) {
            const backgroundColor = Office.context.officeTheme.bodyBackgroundColor

            // Convert hex to RGB and calculate brightness
            const hex = backgroundColor.replace('#', '')
            const r = Number.parseInt(hex.substring(0, 2), 16)
            const g = Number.parseInt(hex.substring(2, 4), 16)
            const b = Number.parseInt(hex.substring(4, 6), 16)

            // Calculate relative luminance
            const brightness = (r * 299 + g * 587 + b * 114) / 1000

            // If brightness is low, it's a dark theme
            return brightness < 128 ? 'dark' : 'light'
        }

        // Fallback: check system preference
        if (globalThis.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
            return 'dark'
        }
    } catch (error) {
        console.warn('Error detecting Office theme:', error)
    }

    return null
}

// Function to detect system theme preference
const detectSystemTheme = (): 'light' | 'dark' => {
    if (globalThis.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
        return 'dark'
    }
    return 'light'
}

export const useTheme = (initialMode: ThemeMode = 'auto'): UseThemeResult => {
    const [themeMode, setThemeMode] = useState<ThemeMode>(initialMode)
    const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
        // In development/browser, prioritize system theme over Office theme
        const isInOffice = typeof Office !== 'undefined' && Office.context?.host
        if (isInOffice) {
            return detectOfficeTheme() ?? detectSystemTheme()
        }
        return detectSystemTheme()
    })

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = globalThis.matchMedia?.('(prefers-color-scheme: dark)')

        if (!mediaQuery) return undefined

        const handleThemeChange = (event: MediaQueryListEvent) => {
            const newSystemTheme = event.matches ? 'dark' : 'light'
            setSystemTheme(newSystemTheme)
        }

        mediaQuery.addEventListener('change', handleThemeChange)

        return () => {
            mediaQuery.removeEventListener('change', handleThemeChange)
        }
    }, [])

    // Listen for Office theme changes (if supported)
    useEffect(() => {
        const isInOffice = typeof Office !== 'undefined' && Office.context?.host
        
        if (isInOffice) {
            const checkOfficeTheme = () => {
                const officeTheme = detectOfficeTheme()
                if (officeTheme && officeTheme !== systemTheme) {
                    setSystemTheme(officeTheme)
                }
            }

            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    checkOfficeTheme()
                }
            }
            
            document.addEventListener('visibilitychange', handleVisibilityChange)
            // Initial check in case theme changed while not focused
            checkOfficeTheme()
            
            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange)
            }
        }
        
        // Return empty cleanup function for non-Office environments
        return () => {}
    }, [systemTheme])

    // Determine the actual theme to use
    const isDarkMode = themeMode === 'auto' ? systemTheme === 'dark' : themeMode === 'dark'
    const theme = isDarkMode ? customDarkTheme : webLightTheme

    // Apply dark class to document element for Tailwind CSS dark mode
    useEffect(() => {
        const htmlElement = document.documentElement
        if (isDarkMode) {
            htmlElement.classList.add('dark')
        } else {
            htmlElement.classList.remove('dark')
        }
    }, [isDarkMode])

    return {
        theme,
        themeMode,
        isDarkMode,
        setThemeMode,
    }
}